import React, { ReactNode } from 'react';
import { Alert } from '@mantine/core';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { MdCheckCircle, MdError, MdInfo } from 'react-icons/md';
import type { Tone } from '../../types/Tone';
import './Hint.scss';

// Фасад на Mantine Alert. Публичный IHintProps сохранён ради ~40 потребителей:
// tone/icon/iconMapping/button/maxWidth маппятся в нативные Alert-пропы, легаси-разметка
// (кастомные div'ы + свой крестик) заменена на Alert (CloseButton, a11y).
// BEM-классы rf-hint__* сохранены через classNames-маппинг слотов — внешние SCSS-консьюмеры
// (WinnerSelection ×7 min-width, dialog.scss overflow-wrap у __title) живут без правок.
// Палитра тонов — проектные токены в Hint.scss (не Mantine color-scheme).

/** Смысловой цвет плашки. */
export type HintTone = Extract<Tone, 'neutral' | 'info' | 'success' | 'warning' | 'danger'>;

export interface IHintProps {
    /** Основное сообщение */
    children?: ReactNode | string;
    /** Заголовок */
    title?: ReactNode | string;
    /** Текст кнопки */
    button?: ReactNode;
    /** Смысловой цвет сообщения @default 'neutral' */
    tone?: HintTone;
    /** Сопоставление тона с иконкой по умолчанию. */
    iconMapping?: Record<HintTone, ReactNode>;
    /** Использовать собственную иконку.
     * Если передать `false`, то иконка скроется.
     * */
    icon?: ReactNode;
    /** Максимальная ширина */
    maxWidth?: string;
    /** CSS класс */
    className?: string;
    onClose?: () => void;
    /**
     * Роль для скринридера. По умолчанию `note` — статичное пояснение, диктор читает его по месту. `alert` — сообщение,
     * которое появляется в ответ на действие (ошибка отправки или загрузки, предупреждение о последствиях выбора,
     * результат операции): объявляется сразу при появлении.
     * @default 'note'
     */
    role?: 'alert' | 'note' | 'status';
}

const ICON_MAPPING: Record<HintTone, ReactNode> = {
    neutral: <MdInfo />,
    danger: <MdError />,
    success: <MdCheckCircle />,
    info: <MdInfo />,
    warning: <MdInfo />,
};

// Модификатор класса — прежнее имя цвета: `rf-hint--red` и др. остаются в стилях и разметке.
const TONE_CLASS: Record<HintTone, string> = {
    neutral: 'default',
    info: 'blue',
    success: 'green',
    warning: 'yellow',
    danger: 'red',
};

// Слоты Alert → легаси-BEM: внешние стили и тесты продолжают находить rf-hint__*.
const CLASS_NAMES = {
    root: 'rf-hint',
    wrapper: 'rf-hint__wrapper',
    icon: 'rf-hint__icon',
    body: 'rf-hint__body',
    title: 'rf-hint__title',
    message: 'rf-hint__message',
    closeButton: 'rf-hint__close',
} as const;

const Hint: React.FC<IHintProps> = ({
    children,
    button,
    tone = 'neutral',
    iconMapping = ICON_MAPPING,
    icon,
    title,
    maxWidth = '648px',
    onClose,
    className,
    role = 'note',
}: IHintProps) => {
    // Паритет легаси-семантики icon: false — скрыть; undefined/строка — иконка тона.
    const iconNode =
        icon === false ? undefined : icon === undefined || typeof icon === 'string' ? iconMapping[tone] : icon;

    // Строковый title рендерим как sanitized-HTML (в конфигах приходит разметка, в т.ч. ссылки).
    const titleNode =
        title == null ? undefined : typeof title === 'string' ? (
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title, { ADD_ATTR: ['target'] }) }} />
        ) : (
            title
        );

    // message-слот рендерим только при непустом контенте (легаси-условие children &&).
    const body =
        children || button ? (
            <>
                {children}
                {button && <div className="rf-hint__button">{button}</div>}
            </>
        ) : undefined;

    return (
        <Alert
            classNames={CLASS_NAMES}
            className={clsx(`rf-hint--${TONE_CLASS[tone]}`, className)}
            style={{ maxWidth }}
            icon={iconNode}
            title={titleNode}
            withCloseButton={!!onClose}
            onClose={onClose}
            closeButtonLabel="Закрыть"
            role={role}
            data-testid="rf-hint"
        >
            {body}
        </Alert>
    );
};

export default Hint;
